/* eslint-disable react/prop-types */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { memo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import useFetch from 'use-http';
import Masonry from 'react-masonry-css';
import { isNumber } from 'underscore';
import { makeStyles } from '@fluentui/react-components';
import { ErrorMessage500 } from './error-messages';
import { generateGuid } from '@bingads-webui-universal/primitive-utilities';
import { StyledErrorPage } from './error-page';

const useStyles = makeStyles({
  noResultWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '100px',
  },
});

export const withSearchResults = (apiPath, loadingComponent, resultCardComponent) => memo(({
  queryString,
  queryInput,
  queryType,
  searchConfig,
  count,
  setCount,
  logger,
  perfMarker,
  setFetchComplete,
  noResultsComponent,
}) => {
  const classes = useStyles();
  const [results, setResults] = useState({});
  const [lastRID, setLastRID] = useState(-1);
  const [massonryCols, setMassonryCols] = useState(3);
  const {
    get,
    response,
    loading,
    error,
  } = useFetch();
  const handleResize = useCallback(() => {
    if (window.innerWidth < 950) {
      setMassonryCols(1);
    } else if (window.innerWidth < 1200) {
      setMassonryCols(2);
    } else {
      setMassonryCols(3);
    }
  }, []);

  async function loadResults() {
    const requestId = generateGuid();
    setLastRID(requestId);
    logger.logTrace(`Start fetching results for '${queryInput}'`, {
      RID: requestId,
      QueryString: queryString,
    });
    const result = await get(`${apiPath}${queryString}&ReqId=${requestId}`);
    perfMarker.willRender();
    setFetchComplete(true);
    if (response.ok) {
      logger.logTrace(`Succeed to fetch results for '${queryInput}'`, {
        RID: requestId,
        QueryString: queryString,
        Response: result,
      });
      setResults(result);
    }
  }

  useEffect(() => {
    perfMarker.willFetchData();
    loadResults();
  }, [queryString]);

  useEffect(() => {
    if (isNumber(results['@odata.count'])) setCount(results['@odata.count']);
  }, [results['@odata.count']]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (error) {
    logger.logError(`Failed to fetch results for '${queryInput}'`, {
      RID: lastRID,
      QueryString: queryString,
      Error: error,
    });
    if (response.status === 500) return <ErrorMessage500 />;
    return <StyledErrorPage />;
  }
  if (loading) return loadingComponent;

  return count === 0 ? (
    <div className={classes.noResultWrapper}>
      {noResultsComponent}
    </div>
  ) : (
    <Masonry
      breakpointCols={massonryCols}
      className="masonry-grid"
      columnClassName="masonry-grid_column"
    >
      {
        results.value && results.value.map((result) => {
          const updatedResult = { ...result };
          if (updatedResult.AdId) updatedResult.AdId = updatedResult.AdId.toString();
          if (updatedResult.AdvertiserId) updatedResult.AdvertiserId = updatedResult.AdvertiserId.toString();
          return React.cloneElement(resultCardComponent, { key: updatedResult.AdId || updatedResult.AdvertiserId, ...updatedResult, searchConfig });
        })
      }
    </Masonry>
  );
}, (prevProps, nextProps) => prevProps.queryString === nextProps.queryString && prevProps.count === nextProps.count);

withSearchResults.PropTypes = {
  apiPath: PropTypes.string.isRequired,
  loadingComponent: PropTypes.element.isRequired,
  resultCardComponent: PropTypes.element.isRequired,
};

withSearchResults().PropTypes = {
  queryString: PropTypes.string.isRequired,
  queryInput: PropTypes.string.isRequired,
  searchConfig: PropTypes.object.isRequired,
  count: PropTypes.number.isRequired,
  noResultsComponent: PropTypes.element,
};
